import { redirect } from 'react-router';

import { validateEmail } from '@/actions/address';
import { fetchSignupDomain } from '@/actions/domain';
import { ActionContext, LegacyRoute } from '@/routes/routesMiddleware';
import { clearCache, getCache, setCache } from '@/utils';
import { sameOriginPath } from '@/utils/sameOriginPath';

import AwaitingEmailValidationPage from './AwaitingEmailValidationPage';
import EmailValidatedPage from './EmailValidatedPage';

const TOKEN_EXPIRED_ERROR = 'GraphQL Error: expired token';

// Where to send the user once their email is validated, e.g. back to /responses
// (ENG-5808). localStorage, not sessionStorage: the validation link in the email
// usually opens in a new tab.
const VALIDATION_NEXT_KEY = 'email_validation_next';

// Hard navigation, because `next` may be served outside this app's router (the
// /responses proxy), where a router redirect would render our not-found page.
function assignSavedNext(): boolean {
  let next: string | undefined;
  try {
    const saved = getCache(VALIDATION_NEXT_KEY);
    next = typeof saved === 'string' ? sameOriginPath(saved) : undefined;
  } catch {
    // Malformed JSON in storage: treat it as nothing saved.
  }
  clearCache(VALIDATION_NEXT_KEY);
  if (!next) return false;
  window.location.assign(next);
  return true;
}

const routes: LegacyRoute[] = [
  {
    path: '/validate_email',
    public: true,
    element: <div>Link expired.</div>,
    async action({ store, query }: ActionContext): Promise<Response | null> {
      const token = query.get('validate_token');
      document.title = 'OnFrontiers';

      if (!token) return redirect('/dashboard');

      try {
        await store.dispatch(validateEmail(token));
      } catch (err: unknown) {
        if (err instanceof Error && err.message === TOKEN_EXPIRED_ERROR) {
          return null;
        }
        throw err;
      }

      return redirect('/email_validated');
    },
  },
  {
    //subdomain: '*',
    path: '/awaiting_email_validation',
    public: false,
    element: <AwaitingEmailValidationPage />,
    async action({ store, subdomain, query }: ActionContext): Promise<Response | null> {
      document.title = 'Validate Email Address';
      // Always overwrite, so a visit without `next` clears a stale one.
      setCache(VALIDATION_NEXT_KEY, sameOriginPath(query.get('next')));

      if (subdomain) {
        const domain = await store.dispatch(fetchSignupDomain(subdomain));
        if (!domain) return redirect('/dashboard');
      }

      const { viewer } = store.getState();
      if (viewer.email && viewer.email.accepted) {
        // Stay pending until the page unloads, so the "check your inbox" copy
        // never flashes for a viewer who is already verified.
        if (assignSavedNext()) return new Promise(() => {});
        return redirect('/dashboard');
      }
      return null;
    },
  },
  {
    path: '/email_validated',
    public: false,
    element: <EmailValidatedPage />,
    async action(): Promise<null> {
      document.title = 'Email Validated';
      assignSavedNext();
      return null;
    },
  },
];

export default routes;
